<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, RouterLink } from 'vue-router';
import api from '@/services/axios';
import { useI18n } from '@/i18n';
import { tokenStore } from '@/services/tokenStore';
import { passkeyService } from '@/services/passkeyService';

// PrimeVue Komponenten
import Password from 'primevue/password';
import LayoutFooter from '@/components/layout/layoutFooter.vue';
import pCheckbox from 'primevue/checkbox';
import pInputText from 'primevue/inputtext';
import pButton from 'primevue/button';
import pFloatLabel from 'primevue/floatlabel';
import pCard from 'primevue/card';

const router = useRouter();
const { t } = useI18n();
const email = ref('');
const password = ref('');
const error = ref('');
const loading = ref(false);
const rememberMe     = ref(true); // Default: eingeloggt bleiben (localStorage) — früher war Token immer persistent
const passkeySupported = ref(false);
const passkeyLoading   = ref(false);

// ── MFA (zweiter Login-Schritt) ────────────────────────────────────────────────
const MFA_DEVICE_KEY = 'secsu-mfa-device-token';
const mfaStep       = ref(false);   // true → OTP-Schritt statt Passwort-Formular
const mfaToken      = ref('');      // kurzlebige Challenge vom Server
const otpCode       = ref('');
const rememberDevice = ref(false);
const mfaLoading    = ref(false);

function getDeviceToken() {
  try { return localStorage.getItem(MFA_DEVICE_KEY) || undefined; } catch { return undefined; }
}
function setDeviceToken(tok) {
  try { if (tok) localStorage.setItem(MFA_DEVICE_KEY, tok); } catch { /* ignore */ }
}

async function finishLogin(token) {
  // tokenStore kümmert sich um localStorage vs. sessionStorage (Web)
  // und Keychain (Native iOS). Keine direkten Storage-Zugriffe hier.
  await tokenStore.set(token, rememberMe.value);
  router.push('/');
}

const login = async () => {
  error.value = '';
  loading.value = true;

  try {
    const response = await api.post('/auth/login', {
      email: email.value,
      password: password.value,
      rememberMe: rememberMe.value,
      mfa_device_token: getDeviceToken(),
    });

    // MFA aktiv → zweiter Schritt (kein Token in dieser Antwort)
    if (response.data?.mfa_required) {
      mfaToken.value = response.data.mfa_token;
      otpCode.value = '';
      rememberDevice.value = false;
      mfaStep.value = true;
      return;
    }

    const { token } = response.data;
    if (!token) throw new Error('No token in login response');
    await finishLogin(token);
  } catch (err) {
    if (err.response?.status === 401) {
      error.value = t('login.invalidCredentials');
    } else if (err.response?.status === 429) {
      error.value = err.response?.data?.message || t('login.loginError');
    } else {
      error.value = t('login.loginError');
    }
  } finally {
    loading.value = false;
  }
};

// Zweiter Schritt: TOTP- oder Backup-Code verifizieren.
const verifyMfa = async () => {
  error.value = '';
  const code = (otpCode.value || '').trim();
  if (!code) return;
  mfaLoading.value = true;
  try {
    const response = await api.post('/auth/mfa/verify', {
      mfa_token: mfaToken.value,
      code,
      remember_device: rememberDevice.value,
    });
    const { token, mfa_device_token } = response.data;
    if (!token) throw new Error('No token in verify response');
    if (mfa_device_token) setDeviceToken(mfa_device_token);
    await finishLogin(token);
  } catch (err) {
    if (err.response?.status === 401) {
      error.value = t('login.mfaInvalid');
    } else if (err.response?.status === 429) {
      error.value = err.response?.data?.message || t('login.loginError');
    } else {
      error.value = t('login.loginError');
    }
  } finally {
    mfaLoading.value = false;
  }
};

function backToPassword() {
  mfaStep.value = false;
  mfaToken.value = '';
  otpCode.value = '';
  error.value = '';
}

// ── Passkey-Login ─────────────────────────────────────────────────────────────
const loginWithPasskey = async () => {
  if (passkeyLoading.value) return;
  error.value = '';
  passkeyLoading.value = true;
  try {
    // E-Mail falls schon eingetippt mitgeben → gezielter Passkey-Prompt
    const token = await passkeyService.authenticate(email.value || '');
    // Passkey-Login ist immer persistent (7-Tage-JWT vom Server)
    await tokenStore.set(token, true);
    router.push('/');
  } catch (err) {
    // AbortError / NotAllowedError = User hat Dialog geschlossen → kein Fehler
    if (err?.name !== 'AbortError' && err?.name !== 'NotAllowedError') {
      error.value = t('passkey.authError');
    }
  } finally {
    passkeyLoading.value = false;
  }
};

onMounted(async () => {
  passkeySupported.value = await passkeyService.isSupported();
});
</script>

<template>
  <div class="flex-container">
    <pCard class="card-container">
      <template #title>
        <img src="/logo.png" alt="Logo" class="logo mx-auto" />
        <pDivider />
      </template>

     <template #content>
    <form @submit.prevent="mfaStep ? verifyMfa() : login()" class="login-form" style="padding-top: 30px">
    <div class="p-grid p-fluid login-grid">

      <!-- Fehlermeldung (beide Schritte) -->
      <div v-if="error" class="p-col-12 login-row">
        <p class="p-error text-center">{{ error }}</p>
      </div>

      <!-- ── Schritt 1: Anmeldedaten ──────────────────────────────────────── -->
      <template v-if="!mfaStep">
        <!-- Benutzername -->
        <div class="p-col-12 login-row">
          <pFloatLabel class="LoginInput">
        <pInputText
          id="email"
          v-model="email"
          type="email"
          required
          placeholder=" "
          class="w-full"
          autocapitalize="none"
          autocorrect="off"
          autocomplete="username"
          spellcheck="false"
        />
        <label for="email">{{ t('login.email') }}</label>
          </pFloatLabel>
        </div>

        <!-- Passwort -->
        <div class="p-col-12 login-row">
          <pFloatLabel class="LoginInput">
            <Password
              id="password"
              v-model="password"
              :feedback="false"
              placeholder=" "
              class="w-full"
              inputClass="w-full"
              autocomplete="current-password"
              :pt="{ input: { autocapitalize: 'none', autocorrect: 'off', spellcheck: 'false' } }"
            />
            <label for="password">{{ t('login.password') }}</label>
          </pFloatLabel>
        </div>

        <!-- Remember Me -->
        <div class="p-col-12 login-row">
          <div class="flex items-left gap-2">
            <pCheckbox v-model="rememberMe" inputId="rememberMe" binary />
            <label for="rememberMe" class="ml-2">{{ t('login.rememberMe') }}</label>
          </div>
        </div>

        <!-- Anmelden-Button -->
        <div class="p-col-12 login-row">
          <pFloatLabel class="LoginInput">
            <pButton type="submit" :label="t('login.loginBtn')" :loading="loading" class="w-full" />
          </pFloatLabel>
        </div>

        <!-- Passkey-Login (nur wenn Gerät Face ID / Touch ID unterstützt) -->
        <div v-if="passkeySupported" class="p-col-12 login-row passkey-row">
          <div class="passkey-divider">
            <span>{{ t('login.orDivider') }}</span>
          </div>
          <pButton
            type="button"
            :label="t('passkey.loginBtn')"
            icon="pi pi-face-smile"
            severity="secondary"
            outlined
            :loading="passkeyLoading"
            class="w-full passkey-btn"
            @click="loginWithPasskey"
          />
        </div>

        <!-- Passwort vergessen -->
        <div class="p-col-12 login-row text-center forgot-password-row">
          <RouterLink to="/auth/forgot-password" class="forgot-password-link">
            {{ t('login.forgotPassword') }}
          </RouterLink>
        </div>
      </template>

      <!-- ── Schritt 2: Zwei-Faktor-Code ──────────────────────────────────── -->
      <template v-else>
        <div class="p-col-12 login-row mfa-head">
          <i class="pi pi-shield mfa-head-icon" />
          <div>
            <p class="mfa-head-title">{{ t('login.mfaTitle') }}</p>
            <p class="mfa-head-help">{{ t('login.mfaHelp') }}</p>
          </div>
        </div>

        <div class="p-col-12 login-row">
          <pFloatLabel class="LoginInput">
            <pInputText
              id="otp"
              v-model="otpCode"
              inputmode="numeric"
              autocomplete="one-time-code"
              placeholder=" "
              class="w-full otp-input"
              autofocus
            />
            <label for="otp">{{ t('login.otpLabel') }}</label>
          </pFloatLabel>
        </div>

        <div class="p-col-12 login-row">
          <div class="flex items-left gap-2">
            <pCheckbox v-model="rememberDevice" inputId="rememberDevice" binary />
            <label for="rememberDevice" class="ml-2">{{ t('login.rememberDevice') }}</label>
          </div>
        </div>

        <div class="p-col-12 login-row">
          <pButton type="submit" :label="t('login.verifyBtn')" :loading="mfaLoading" class="w-full" />
        </div>

        <div class="p-col-12 login-row text-center forgot-password-row">
          <a href="#" class="forgot-password-link" @click.prevent="backToPassword">{{ t('login.mfaBack') }}</a>
        </div>
      </template>
    </div>
  </form>
</template>
      <template #footer>
        <LayoutFooter />
      </template>
    </pCard>
  </div>
</template>

<style scoped>
.flex-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  margin: 0;
  padding: 0;
}

.p-float-label {
  width: 100%;
}

.p-card {
  width: 400px;
  padding: 20px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
}

.logo {
  max-width: 200px;
  width: 100%;
  display: block;
  margin: 0 auto;
}

.p-floatlabel label {
	color: var(--p-text-color) !important;
}
/* Vue 3 SFC - :deep() */
:deep(.p-inputtext),
:deep(input[type="text"]),
:deep(input[type="password"]),
:deep(textarea) {
  /* Nutze deine Theme-Variablen */
  color: var(--p-text-color) !important;
  background: var(--p-surface-card) !important;
  caret-color: var(--p-text-color) !important;
}

/* Placeholder lesbar machen */
:deep(.p-inputtext::placeholder),
:deep(input::placeholder),
:deep(textarea::placeholder) {
  color: rgba(255,255,255,0.6) !important; /* oder var(--p-text-muted) */
  opacity: 1 !important;
}

/* Autofill (Chrome) */
:deep(input:-webkit-autofill),
:deep(input:-webkit-autofill::first-line) {
  -webkit-text-fill-color: var(--p-text-color) !important;
}

/* Falls du ::v-deep brauchst statt :deep */
::v-deep .p-inputtext,
::v-deep input[type="text"],
::v-deep input[type="password"],
::v-deep textarea {
  color: var(--p-text-color) !important;
}
.p-error {
  color: red;
}

/* größere Lücke zwischen Zeilen */
.login-row {
  margin-bottom: 1.5rem; /* ca. 24px Abstand - anpassen nach Geschmack */
}

/* full width guarantee für pFloatLabel / pInputText */
.LoginInput {
  display: block;
  width: 100%;
}

/* Sicherstellen, dass PrimeVue Inputs 100% belegen */
.LoginInput .p-inputtext,
.LoginInput input,
.LoginInput .p-password input {
  width: 100% !important;
  box-sizing: border-box;
}

/* Feinabstimmung: Button/Checkbox-Abstand */
.login-row .p-button,
.login-row .p-checkbox {
  width: 100%;
}

/* Passwort-vergessen Link */
.forgot-password-row {
  margin-top: -0.5rem;
}
.forgot-password-link {
  color: var(--p-primary-color);
  text-decoration: none;
  font-size: 0.9rem;
}
.forgot-password-link:hover {
  text-decoration: underline;
}

/* Passkey-Divider */
.passkey-row {
  margin-top: -0.25rem;
}

.passkey-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0.75rem;
  color: var(--p-text-color-secondary);
  font-size: 0.78rem;
}

.passkey-divider::before,
.passkey-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--p-content-border-color, rgba(0,0,0,.12));
}

.passkey-btn {
  justify-content: center;
}

/* Optional: auf kleinen Bildschirmen etwas mehr padding */
@media (max-width: 480px) {
  .login-row {
    margin-bottom: 1.25rem;
  }
}

/* ── MFA-Schritt ─────────────────────────────────────────────────────────── */
.mfa-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
}
.mfa-head-icon {
  font-size: 1.4rem;
  color: var(--p-primary-color, #3b82f6);
  margin-top: 2px;
}
.mfa-head-title {
  margin: 0 0 2px;
  font-weight: 700;
  color: var(--p-text-color);
}
.mfa-head-help {
  margin: 0;
  font-size: .82rem;
  color: var(--p-text-color-secondary);
  line-height: 1.4;
}
.otp-input :deep(input),
.otp-input {
  letter-spacing: 4px;
}
</style>
